/**
 * Client-side currency helper — mirrors `backend/localisation.py`.
 * Preloads the catalog on app boot and caches in localStorage.
 */
import api from "@/lib/api";

const CACHE_KEY = "smin_currencies_v1";
let CACHE = null;

const FALLBACK = [
  { code: "USD", name: "US Dollar", symbol: "$", usd_rate: 1.00 },
];

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch { return null; }
}

export async function preloadCurrencies() {
  if (CACHE) return CACHE;
  const cached = readCache();
  if (cached) CACHE = cached;
  try {
    const { data } = await api.get("/currencies");
    if (Array.isArray(data.currencies) && data.currencies.length) {
      CACHE = data.currencies;
      localStorage.setItem(CACHE_KEY, JSON.stringify(CACHE));
    }
  } catch {
    if (!CACHE) CACHE = FALLBACK;
  }
  return CACHE;
}

export function listCurrencies() {
  return CACHE || readCache() || FALLBACK;
}

export function getCurrency(code) {
  const list = listCurrencies();
  const found = list.find((c) => c.code === (code || "USD").toUpperCase());
  return found || list[0];
}

/**
 * Format a USD-denominated value into the target project currency.
 *   formatMoney(1_250_000, { currency: 'EUR', rate: 1.10, digits: 0 })
 *   -> "€1,136,364"
 */
export function formatMoney(usdValue, opts = {}) {
  const cur = getCurrency(opts.currency || "USD");
  const rate = opts.rate ?? cur.usd_rate ?? 1.0;
  const converted = Number(usdValue) / (rate || 1);
  const digits = opts.digits ?? (Math.abs(converted) >= 1000 ? 0 : 2);
  const nf = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
  const compact = opts.compact && Math.abs(converted) >= 1_000_000;
  if (compact) {
    const m = converted / 1_000_000;
    return `${cur.symbol}${m.toFixed(2)}M`;
  }
  return `${cur.symbol}${nf.format(converted)}`;
}
